/* eslint-disable no-underscore-dangle, max-statements */
import {createStore} from 'redux-dynamic-modules';
import {getSagaExtension} from '@optimaxdev/redux-dynamic-modules-saga';
import {applyMiddleware, compose, combineReducers} from 'redux';
import promiseMiddleware from 'redux-promise-middleware';
import createSagaMiddleware from 'redux-saga';
import {createBrowserHistory, createMemoryHistory} from 'history';
import {routerMiddleware} from 'connected-react-router';
import {persistStore, persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {errorMiddleware} from 'libs/errorMiddleware/errorMiddleware';

import {logWithSentry, createSentryReduxEnhancer} from 'libs/sentry/sentry';

import rootReducer, {reducers} from '../reducers';
import rootSaga from '../sagas';
import {CookiesManager} from './cookie/cookiesManager';

export const history = __NODEJS__ ? createMemoryHistory() : createBrowserHistory();
const createSagaMiddlewareOptions = {
    onError(error) {
        if (process.env.APP_ENV === 'production') {
            logWithSentry(error);
        } else {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    },
};

const routeMiddleware = routerMiddleware(history);

const persistConfig = {
    key: 'glassesusa',
    storage,
    whitelist: ['reorder', 'currentShippingAddress', 'currentBillingAddress'],
};

/**
 * Return array of default middlewares
 *
 * @export
 * @returns {array} - array of default middlewares
 */
export function getDefaultMiddlewares() {
    return [
        errorMiddleware,
        promiseMiddleware({
            promiseTypeDelimiter: '_',
        }),
        routeMiddleware,
    ];
}

/**
 * Configures redux store for server-side rendering environment
 * @param {any} initialState - initial state of the store
 * @param {any} initialHistory - initial history config
 * @param {CookiesManager} [cookiesManager] - instance that contains and provide cookies data
 * @returns {any} configured store
 */
export function configureSsrStore(initialState, initialHistory, cookiesManager?: CookiesManager) {
    history.push(initialHistory.url, initialHistory.state);
    const reduxSaga = createSagaMiddleware({
        ...createSagaMiddlewareOptions,
        context: {cookies: cookiesManager ? cookiesManager.getAll() : {}},
    });

    const rootReducerModule = {
        id: 'root',
        reducerMap: rootReducer(history),
    };

    const store = createStore(
        {
            initialState,
            enhancers: [applyMiddleware(...getDefaultMiddlewares(), reduxSaga)],
            extensions: [getSagaExtension({middlewares: getDefaultMiddlewares()})],
        },
        rootReducerModule,
    );

    return {store, reduxSaga};
}

/**
 * Add middleware and enable hot loading
 *
 * @param {{}} initialState State before applying middleware etc
 * @param {{}} restEnhancers additional enhancers
 * @returns {{}} React store
 */
export default function configureStore(initialState, restEnhancers = []) {
    let emitter;
    if (process.env.SAGA_MONITOR === '1' && process.env.NODE_ENV === 'development') {
        const {ReduxSagaEmitter} = require('kuker-emitters');
        emitter = ReduxSagaEmitter();
        // kuker devtools for sagas debug
        // https://github.com/krasimir/kuker#integration-with-redux-saga
        createSagaMiddlewareOptions.sagaMonitor = emitter.sagaMonitor;
    }
    const sagaMiddleware = createSagaMiddleware(createSagaMiddlewareOptions);

    const rootReducerModule = {
        id: 'root',
        reducerMap: rootReducer(history),
        sagas: [rootSaga],
    };
    /* setup of https://github.com/zalmoxisus/redux-devtools-extension */
    const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

    const store = createStore(
        {
            initialState,
            ...(process.env.NODE_ENV === 'production'
                ? {}
                : {advancedComposeEnhancers: composeEnhancers}),
            advancedCombineReducers: reducersMap =>
                persistReducer(persistConfig, combineReducers(reducersMap)),
            enhancers: [
                applyMiddleware(...getDefaultMiddlewares(), sagaMiddleware),
                createSentryReduxEnhancer(),
                ...restEnhancers,
            ],
            extensions: [getSagaExtension({middlewares: getDefaultMiddlewares()})],
        },
        rootReducerModule,
    );

    const persistor = persistStore(store);

    /* istanbul ignore next */
    if (module.hot) {
        // Enable Webpack hot module replacement for reducers
        /* istanbul ignore next */
        module.hot.accept('../reducers', () => {
            const nextRootReducer = require('../reducers/index');
            store.replaceReducer(nextRootReducer(history));
        });
    }
    if (process.env.SAGA_MONITOR === '1' && process.env.NODE_ENV === 'development') {
        emitter.setStore(store);
    }

    const {addModules} = store;
    store.addModules = modules => {
        addModules(modules);
        persistor.persist();
    };

    return {store, persistor};
}

/**
 * An enhancer that would be passed to Redux's createStore to set preload state for integration tests.
 *
 * @param {{}} preloadedState preload state
 * @returns {Function} redux enhancer
 */
const setPreloadStateEnhancer = preloadedState => next => {
    if (NODE_ENV !== 'test') {
        return next();
    }
    return (reducer, initialState) => {
        const wrappedReducer = (state, action) => {
            const newState = reducer(state, action);

            if (typeof preloadedState[action.payload] !== 'undefined') {
                newState[action.payload] = preloadedState[action.payload];
            }

            return newState;
        };

        return next(wrappedReducer, initialState);
    };
};

/**
 * Create App store for integration test,
 * which allows you to set the preloaded state
 *
 * @param {{}} preloadedState State before applying middleware etc
 * @export
 * @returns {{store, persistor, sagaTask}} configured store
 */
export const createConfigureStoreForIntegrationTests = preloadedState => {
    const initialState = Object.keys(reducers).reduce((acc, key) => {
        if (preloadedState[key]) {
            return {...acc, [key]: preloadedState[key]};
        }
        return acc;
    }, {});

    return configureStore(initialState, [setPreloadStateEnhancer(preloadedState)]);
};
